/**
 * Esterilização — criação de batch.
 *
 * Operador:
 *   1. Escolhe o equipamento (autoclave/plasma) da lista.
 *   2. Se Bowie-Dick pendente em autoclave a vapor, mostra alerta com CTA Bowie-Dick (RT+).
 *   3. Bipa cada kit a incluir; soma de peso em tempo real contra max_load.
 *   4. POST /v1/batches cria o lote em PREPARING.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, AlertTriangle, Beaker, Send, Trash2, CheckCircle2, ScanLine, ShieldCheck } from 'lucide-react';
import { apiGet, apiPost, ApiError } from '@/shared/api/client';
import { useBarcodeScanner } from '@/shared/scanner/GlobalBarcodeListener';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/shared/auth/AuthProvider';

interface Equipment {
  id: string;
  code: string;
  name: string;
  type: 'AUTOCLAVE_STEAM' | 'AUTOCLAVE_PLASMA' | 'WASHER_DISINFECTOR' | 'ULTRASONIC' | string;
  status: 'AVAILABLE' | 'IN_USE' | 'MAINTENANCE' | 'INTERDICTED' | 'BLOCKED_BOWIE_DICK' | string;
  max_load_kg: number | null;
  requires_bowie_dick: number;
  bowie_dick_pending: number;
  active: number;
}

interface KitByBarcode {
  id: string;
  barcode: string;
  alias?: string | null;
  template_name?: string | null;
  estimated_weight_kg?: number | null;
  is_quarantined?: number;
  current_phase?: string | null;
}

type FeedbackKind = 'ok' | 'warn' | 'err';

export function EsterilizacaoPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canBowieDick = !!user && (user.role === 'ADMIN' || user.role === 'SUPERVISOR' || user.role === 'NURSE_RT');

  const eqQ = useQuery({
    queryKey: ['equipments', 'sterilizers'],
    queryFn: async () => {
      const a = await apiGet<{ items: Equipment[] }>('/v1/equipments?type=AUTOCLAVE_STEAM');
      const b = await apiGet<{ items: Equipment[] }>('/v1/equipments?type=AUTOCLAVE_PLASMA');
      return { items: [...a.items, ...b.items] };
    },
  });

  const [eqId, setEqId] = useState<string | null>(null);
  const eq = useMemo(() => eqQ.data?.items.find((e) => e.id === eqId) ?? null, [eqQ.data, eqId]);

  const [kits, setKits] = useState<KitByBarcode[]>([]);
  const [feedback, setFeedback] = useState<{ kind: FeedbackKind; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const totalWeight = useMemo(() => kits.reduce((s, k) => s + (k.estimated_weight_kg ?? 0), 0), [kits]);
  const overload = eq?.max_load_kg ? totalWeight > eq.max_load_kg : false;

  const flash = useCallback((kind: FeedbackKind, text: string) => setFeedback({ kind, text }), []);

  const onScan = useCallback(async (code: string) => {
    if (!eq) { flash('warn', 'Selecione o equipamento primeiro.'); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (kits.some((k) => k.barcode === code)) { flash('warn', `Kit ${code} já incluído.`); return; }
      const k = await apiGet<KitByBarcode>(`/v1/kit-instances/by-barcode/${encodeURIComponent(code)}`);
      if (k.is_quarantined) { flash('err', `Kit ${k.barcode} em quarentena.`); return; }
      setKits((arr) => [...arr, k]);
      flash('ok', `Kit ${k.alias ?? k.template_name ?? k.barcode} adicionado.`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) flash('err', `Kit ${code} não encontrado.`);
      else flash('err', (e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [eq, busy, kits, flash]);

  useBarcodeScanner({ onScan, minLength: 4, enabled: !!eq });

  const createBatch = useMutation({
    mutationFn: () => apiPost<{ id: string; batch_code: string }>(
      '/v1/batches',
      { equipment_id: eq!.id, kit_instance_ids: kits.map((k) => k.id) },
      { idempotencyKey: `batch-${eq!.id}-${Date.now()}` },
    ),
    onSuccess: (data) => {
      flash('ok', `Lote ${data.batch_code} criado. Avance para Liberação Paramétrica ao terminar o ciclo.`);
      qc.invalidateQueries({ queryKey: ['batches'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setKits([]);
    },
    onError: (e) => {
      if (e instanceof ApiError) flash('err', `${e.code}: ${e.message}`);
      else flash('err', (e as Error).message);
    },
  });

  const bowieDick = useMutation({
    mutationFn: (approved: boolean) => apiPost<{ id: string; new_status: string }>(
      `/v1/equipments/${eq!.id}/bowie-dick`,
      { equipment_id: eq!.id, approved, test_lot: undefined },
      { idempotencyKey: `bd-${eq!.id}-${Date.now()}` },
    ),
    onSuccess: (data) => {
      flash(data.new_status === 'AVAILABLE' ? 'ok' : 'warn', `Bowie-Dick: ${data.new_status}`);
      qc.invalidateQueries({ queryKey: ['equipments'] });
    },
    onError: (e) => flash('err', (e as Error).message),
  });

  const feedbackTone =
    feedback?.kind === 'err' ? 'border-danger bg-danger-bg text-danger animate-pulse'
    : feedback?.kind === 'warn' ? 'border-warning bg-warning-bg text-neutral-900'
    : feedback ? 'border-success bg-success-bg text-success' : '';

  const showBowieDickAlert = eq?.type === 'AUTOCLAVE_STEAM' && eq.requires_bowie_dick === 1 && eq.bowie_dick_pending === 1;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
          Esterilização — Carregamento do lote
        </h1>
        <p className="text-sm text-neutral-500">
          Escolha o equipamento, valide Bowie-Dick (se vapor) e bipe os kits a esterilizar.
        </p>
      </header>

      {feedback ? <div className={`rounded border-2 px-4 py-3 text-md font-medium ${feedbackTone}`}>{feedback.text}</div> : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="Equipamento" subtitle={eqQ.isLoading ? 'Carregando…' : `${eqQ.data?.items.length ?? 0} disponíveis`} />
          <CardBody className="p-0">
            <ul className="max-h-80 divide-y divide-neutral-200 overflow-y-auto dark:divide-neutral-700">
              {(eqQ.data?.items ?? []).filter((e) => e.active === 1).map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => { setEqId(e.id); setKits([]); setFeedback(null); }}
                    className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${eqId === e.id ? 'bg-brand-50 dark:bg-brand-900/30' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{e.name}</p>
                      <p className="font-mono text-xxs text-neutral-500">{e.code} · {e.type}</p>
                    </div>
                    <Badge tone={e.status === 'AVAILABLE' ? 'success' : e.status === 'IN_USE' ? 'info' : 'danger'}>{e.status}</Badge>
                  </button>
                </li>
              ))}
              {!eqQ.isLoading && (eqQ.data?.items?.length ?? 0) === 0 ? (
                <li className="p-6 text-center text-xs text-neutral-500">Nenhum equipamento cadastrado.</li>
              ) : null}
            </ul>
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Carga do lote"
            subtitle={eq ? `${kits.length} kit(s) · ${totalWeight.toFixed(2)} kg / ${eq.max_load_kg ?? '—'} kg` : 'Escolha o equipamento'}
            action={kits.length ? <Button variant="ghost" size="sm" leadingIcon={<Trash2 size={14} />} onClick={() => setKits([])}>Limpar</Button> : null}
          />
          <CardBody className="space-y-3">
            {showBowieDickAlert ? (
              <div className="flex items-start gap-3 rounded border-2 border-warning bg-warning-bg p-3 text-sm text-neutral-900">
                <AlertTriangle className="mt-0.5 shrink-0" size={20} />
                <div className="flex-1">
                  <p className="font-semibold">Bowie-Dick diário pendente</p>
                  <p className="text-xs">A primeira carga do dia em autoclave a vapor exige aprovação do teste Bowie-Dick.</p>
                </div>
                <div className="flex flex-col gap-2">
                  <Button variant="primary" size="sm" leadingIcon={<ShieldCheck size={14} />} disabled={!canBowieDick} onClick={() => bowieDick.mutate(true)}>
                    Aprovar
                  </Button>
                  <Button variant="danger" size="sm" disabled={!canBowieDick} onClick={() => bowieDick.mutate(false)}>
                    Reprovar (interditar)
                  </Button>
                </div>
              </div>
            ) : null}

            {overload ? (
              <div className="rounded border-2 border-danger bg-danger-bg px-3 py-2 text-sm font-semibold text-danger animate-pulse">
                Peso excede o limite do equipamento ({eq?.max_load_kg} kg). Remova kits antes de criar o lote.
              </div>
            ) : null}

            {kits.length === 0 ? (
              <div className="grid place-items-center rounded border border-dashed border-neutral-300 p-10 text-sm text-neutral-500 dark:border-neutral-700">
                <ScanLine size={36} strokeWidth={1.5} className="mb-2 text-neutral-400" />
                {eq ? 'Bipe os kits para incluir no lote.' : 'Selecione um equipamento ao lado.'}
              </div>
            ) : (
              <ul className="max-h-72 divide-y divide-neutral-200 overflow-y-auto rounded border border-neutral-200 dark:divide-neutral-700 dark:border-neutral-700">
                {kits.map((k, idx) => (
                  <li key={k.id} className="flex items-center justify-between gap-3 px-4 py-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="grid h-7 w-7 place-items-center rounded bg-neutral-100 text-xs font-semibold text-neutral-600 dark:bg-neutral-700 dark:text-neutral-200">{idx + 1}</span>
                      <Boxes size={16} className="text-neutral-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{k.alias ?? k.template_name ?? 'Kit'}</p>
                        <p className="font-mono text-xxs text-neutral-500">{k.barcode}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-neutral-500">{(k.estimated_weight_kg ?? 0).toFixed(2)} kg</span>
                      <button
                        type="button"
                        className="ict-focus-ring rounded px-2 py-1 text-xs text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        onClick={() => setKits((arr) => arr.filter((x) => x.id !== k.id))}
                      >Remover</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-700">
        <Button
          variant="primary"
          size="touch"
          leadingIcon={<Send size={18} />}
          loading={createBatch.isPending}
          disabled={!eq || kits.length === 0 || overload || showBowieDickAlert}
          onClick={() => createBatch.mutate()}
        >
          {createBatch.isPending ? 'Criando lote…' : 'Criar lote'}
        </Button>
        {createBatch.isSuccess ? <CheckCircle2 className="text-success" size={20} /> : null}
        <span className="ml-2 inline-flex items-center gap-1 text-xs text-neutral-500"><Beaker size={14} /> ciclo será controlado pelo equipamento físico.</span>
      </div>
    </div>
  );
}
